import React from 'react';
import { Statistic } from '../components/StockTable';
import { useFontSize } from '../contexts/FontSizeContext';
import { getTightCellTextSizeClass } from './fontSizeUtils';

/** Two statistic rows: Pros and Cons (no section category headers). */
export const PROS_CONS_STATISTICS: Statistic[] = [
  { key: 'pros', label: 'Pros', type: 'financial' },
  { key: 'cons', label: 'Cons', type: 'financial' },
];

/**
 * Same width logic as `StockTable` / `ListProsCons` / `TickerProsCons`
 * (statisticsColumnWidthMultiplier 1.15, PRO tags).
 */
export function getProsConsIndicatorsColumnWidthPx(opts: {
  isSmallScreen: boolean;
  isEditingColumns: boolean;
}): number {
  const statisticsColumnWidthMultiplier = 1.15;
  const statistics = PROS_CONS_STATISTICS;
  const proOnlyStatistics = PROS_CONS_STATISTICS.map(s => s.key);
  const longestLabel = statistics.reduce(
    (longest, stat) => (stat.label.length > longest.length ? stat.label : longest),
    ''
  );
  const hasProTags = proOnlyStatistics.length > 0;
  const proTagSpace = hasProTags ? 40 : 0;
  const baseWidth = longestLabel.length * 8;
  const padding = 32;
  const editButtonSpace = opts.isEditingColumns ? 24 : 0;
  const buffer = 20;
  const calculatedWidth = baseWidth + padding + editButtonSpace + buffer + proTagSpace;

  if (opts.isSmallScreen) {
    const compactBaseWidth = longestLabel.length * 5.5;
    const compactPadding = 8;
    const compactEditButtonSpace = opts.isEditingColumns ? 16 : 0;
    const compactBuffer = 12;
    const compactProTagSpace = hasProTags ? 28 : 0;
    const compactWidth =
      compactBaseWidth + compactPadding + compactEditButtonSpace + compactBuffer + compactProTagSpace;
    return Math.max(100, compactWidth * statisticsColumnWidthMultiplier);
  }

  const finalWidth = calculatedWidth * statisticsColumnWidthMultiplier;
  return Math.max(120, finalWidth);
}

/**
 * Month/history column width for single-ticker Pros & Cons tables (`TickerProsCons`, Landing embedded).
 * Matches `TickerProsCons` `monthColWidth` (base × optional single-column shrink × 1.5).
 */
export function getProsConsHistoryMonthColumnWidthPx(opts: {
  isSmallScreen: boolean;
  columnCount: number;
  fullScreen: boolean;
}): number {
  const monthColWidthBase = opts.fullScreen
    ? opts.isSmallScreen
      ? 240
      : 320
    : opts.isSmallScreen
      ? 220
      : 280;
  const monthColWidthUnscaled =
    opts.columnCount === 1 ? Math.round(monthColWidthBase * 0.7) : monthColWidthBase;
  return Math.round(monthColWidthUnscaled * 1.5);
}

export type ProsConsFields = {
  pro_1: string | null;
  pro_2: string | null;
  pro_3: string | null;
  pro_4: string | null;
  pro_5: string | null;
  con_1: string | null;
  con_2: string | null;
  con_3: string | null;
  con_4: string | null;
  con_5: string | null;
};

function nonEmptyLines(items: (string | null | undefined)[]): string[] {
  return items
    .map(s => (s == null ? '' : String(s).trim()))
    .filter(s => s.length > 0);
}

/** Line arrays for the Pros / Cons columns from API pro_1..5 / con_1..5. */
export function prosConsLineArrays(pc: ProsConsFields | null | undefined): {
  pros: string[] | null;
  cons: string[] | null;
} {
  if (!pc) return { pros: null, cons: null };
  const pros = nonEmptyLines([pc.pro_1, pc.pro_2, pc.pro_3, pc.pro_4, pc.pro_5]);
  const cons = nonEmptyLines([pc.con_1, pc.con_2, pc.con_3, pc.con_4, pc.con_5]);
  return {
    pros: pros.length ? pros : null,
    cons: cons.length ? cons : null,
  };
}

/** Numbered items; wrapped lines align with the text column, not the numeral. */
export function ProsConsNumberedList({ lines }: { lines: string[] }) {
  const { fontSize } = useFontSize();
  const textSizeClass = getTightCellTextSizeClass(fontSize); // minimal-but-visible scaling for tight cells
  return (
    <div className={`space-y-1.5 ${textSizeClass} leading-snug`}>
      {lines.map((line, i) => (
        <div key={i} className="flex gap-2">
          <span className="flex-shrink-0 min-w-[1.25rem] text-right tabular-nums text-gray-800 dark:text-text-secondary">
            {i + 1}.
          </span>
          <span className="min-w-0 flex-1 break-words">{line}</span>
        </div>
      ))}
    </div>
  );
}

/** ETFs/ETVs: skip pros/cons sync polling; `prosCons: null` yields "-" in the list UI. */
export function createEtfPlaceholderProsConsRow(tickerSymbol: string, tickerBasicsId: number): {
  tickerSymbol: string;
  tickerBasicsId: number;
  year: null;
  month: null;
  prosCons: null;
} {
  return {
    tickerSymbol,
    tickerBasicsId,
    year: null,
    month: null,
    prosCons: null,
  };
}

